/**
 * MONSTER TOWN — TURNING A PLACE TAG INTO A SPOT. ROUTINE CREW (src/routine/**).
 *
 * A routine entry says `where: 'shop'`. This file is the only thing that knows
 * how to answer that, and it answers in three ways, in order:
 *
 *   1. ASK THE WORLD. `world:landmarksRequest` is answered synchronously by the
 *      world crew with every door in the town. If the world moves the shop, the
 *      neighbours move with it and no data changes. Crews never import each
 *      other, so this is the whole conversation.
 *   2. THE DATA FALLBACK. A bearing and a reach from `src/content/routines.ts`,
 *      resolved against the live grid's own centre and size — nothing here has a
 *      town coordinate baked into it.
 *   3. NULL. An honest "I don't know where that is", which the director reads as
 *      "stay around here and potter". A place tag with no answer costs a kid
 *      nothing (LAW #2); it is not an error and it is never logged as one.
 *
 * NEVER BREAKS ON A PLAYER-BUILT LAYOUT. Every spot this file hands back has
 * been checked against the LIVE placement grid and, if the kid has built on it,
 * pushed outwards to the nearest cell they have not. The kid can wall off the
 * shop door and the neighbours will stand next to the wall — which is the right
 * answer, and is a thing a kid will absolutely try.
 *
 * ===========================================================================
 * "WALKABLE" HAS TWO HALVES, AND THIS FILE USED TO ASK FOR ONLY ONE
 * ===========================================================================
 * A monster standing in the pond, clipped through the plaza wall, or inside the
 * fountain basin was the same bug four times over, and it was here.
 *
 * The town a kid sees is made of TWO things: the placement `Grid` (everything
 * the kid built, which is the only thing `grid.isWalkable` has ever heard of)
 * and the world crew's BAKED STATICS — the pond, the fountain, the cottages,
 * the plaza wall, the fence, the flower beds. Those live exclusively in the
 * world's nav mask; the grid does not know they exist. So `walkableNear` said
 * yes to open water, and it said yes with total confidence.
 *
 * That is not a rare edge either — it is where the data POINTS. `plaza` resolves
 * to the middle of town, which is a stride from the fountain; `pond` resolves to
 * a bearing that lands in the water by design ("the pond is lovely and it is not
 * a door", `src/content/routines.ts`); a shop's spot is two and a half steps
 * back from a door that is set into a wall.
 *
 * So `standable()` below asks BOTH: the kid's grid *and* `isTownWalkable`, the
 * world crew's own published query — the same one its A* routes the kid's own
 * monster with, so a neighbour and a kid now agree about where the ground is.
 * It is a read-only import of one pure function (the pattern `src/catching/`
 * already uses for `navGoTo`); nothing here builds, mutates or refreshes the
 * mask. Before the world has baked one, `isTownWalkable` answers "yes" and this
 * file behaves exactly as it did.
 *
 * ===========================================================================
 * AND ASKING BOTH WAS STILL NOT ENOUGH, BECAUSE A MONSTER IS NOT A POINT
 * ===========================================================================
 * That fix went in, `routine-check` and the day sweep both went green — and the
 * captures still showed a monster in the fountain basin, one with its feet in
 * the pond and one clipped through the plaza wall. The tool and the code were
 * agreeing with each other and both were wrong, which is the same trap the
 * `walkable()` note in `demos.ts` describes one level down.
 *
 * TWO ROUNDING ERRORS, IN THE SAME DIRECTION:
 *
 *  1. `isTownWalkable` is a ONE-METRE grid and it is sampled at CELL CENTRES.
 *     A cell counts as open when its centre clears the blocker, and a monster
 *     standing anywhere in that cell answers with the same cell. So a position
 *     up to half a metre INTO the blocker still gets a yes.
 *  2. The mask's own margin (`CLEARANCE`, 0.45 in `src/world/nav.ts`) is half a
 *     monster measured for ROUTING — enough that a path does not cut a corner,
 *     nowhere near enough that a monster standing still is clear of the art.
 *
 *  Together: worst case the answer is yes with the monster's CENTRE five
 *  centimetres inside the fountain rectangle. Measured against the pictures, a
 *  neighbour's body is about 1.4 m across, so "yes" was routinely a monster
 *  drawn 70 cm into the water.
 *
 * A MONSTER IS A CIRCLE, SO ASK LIKE ONE. `standable()` now tests the point AND
 * eight samples on a `BODY`-radius ring around it. Eight is not arbitrary: every
 * blocker in the town is an axis-aligned rectangle, so N/E/S/W always aims a
 * sample straight at the face a monster could be clipping through, and the
 * diagonals catch the corners. Worked through the two rounding errors above, a
 * spot that passes has its centre at least `BODY - 0.05` clear of the real
 * rectangle — i.e. the art does not overlap.
 *
 * NOTHING IS EVER REFUSED (LAW #2). `walkableNear` looks for a spot that clears
 * the whole body first, and only if there is none within reach does it fall back
 * to the old point test, and only then to "stand where you are". A neighbour
 * always gets an answer; it is just the best one available rather than the first
 * one that technically fits.
 */

import { bus } from '../core/events'
import type { Grid } from '../core/grid'
import type { SceneNode } from '../core/scene'
import type { Vec3 } from '../core/types'
import { HOME_REACH, ROUTINE_PLACES } from '../content/routines'
import { townCentre } from '../npc/registry'
import { isTownWalkable } from '../world/nav'

// ---------------------------------------------------------------------------
// the world's landmarks, cached until the world says otherwise
// ---------------------------------------------------------------------------

interface Landmark {
  id: string
  at: Vec3
}

let cache: Landmark[] | null = null

/** Ask the world crew where its doors are. Synchronous reply; cached. */
function landmarks(): Landmark[] {
  if (cache !== null) return cache
  const out: Landmark[] = []
  const off = bus.on('world:landmarks', (e) => {
    for (const l of e.list) out.push({ id: l.id, at: { x: l.at.x, y: 0, z: l.at.z } })
  })
  bus.emit('world:landmarksRequest', {})
  off()
  // An empty reply is not cached: the world crew may simply not have finished
  // building the town yet, and a cached "there is nothing" would last for ever.
  if (out.length === 0) return []
  cache = out
  return cache
}

/** The town was rebuilt (slot switch, a fresh plot). Ask again next time. */
export function forgetLandmarks(): void {
  cache = null
}

// ---------------------------------------------------------------------------
// standable spots
// ---------------------------------------------------------------------------

/**
 * HOW MUCH ROOM A NEIGHBOUR TAKES UP, in world units, measured off the captures
 * rather than guessed: a monster is about 1.4 m across at the town's scale, so
 * 0.7 is the art and 0.75 is the art plus its ink outline.
 *
 * This is a SPACING number, not a collision number — the walker is still the
 * walker and nothing here makes a monster solid. It only decides where somebody
 * is allowed to come to REST, which is the only thing a still photograph sees.
 */
const BODY = 0.75

/** Eight compass points at `BODY`. Built once; see the header for why eight. */
const BODY_RING: readonly (readonly [number, number])[] = Array.from({ length: 8 }, (_, i) => {
  const a = (i / 8) * Math.PI * 2
  return [Math.cos(a) * BODY, Math.sin(a) * BODY] as const
})

/**
 * Is this exact POINT on open ground? Both halves of the question, but asked of
 * one point — which is what a nav mask can answer and what a monster is not.
 * Kept as the last-resort test inside `walkableNear`; everything else asks
 * `standable`.
 */
function pointStandable(grid: Grid, x: number, z: number): boolean {
  const c = grid.worldToCell(x, z, 0)
  if (c.gx < 0 || c.gz < 0 || c.gx >= grid.spec.width || c.gz >= grid.spec.depth) return false
  if (!grid.isWalkable(c.gx, c.gz)) return false
  // The world's baked statics: the pond, the fountain, the cottages, the wall.
  return isTownWalkable(x, z)
}

/**
 * CAN A MONSTER STAND HERE WITHOUT ANY OF IT BEING INSIDE SOMETHING? The whole
 * question — see the header for why the point test alone was not it.
 *
 * Total and cheap: nine array reads and a bounds test, no allocation. Safe to
 * call in a loop, and it is (`walkableNear` and `spreadSpot` both do).
 */
export function standable(grid: Grid, x: number, z: number): boolean {
  if (!pointStandable(grid, x, z)) return false
  for (const [dx, dz] of BODY_RING) {
    // The ring is asked of the world's mask alone, which already folds in the
    // kid's own grid (`isTownWalkable` consults it). One question, one answer.
    if (!isTownWalkable(x + dx, z + dz)) return false
  }
  return true
}

/**
 * ============ A WALK IS PHOTOGRAPHED TOO ============
 *
 * Once nobody was left STANDING in anything, every remaining frame with a
 * monster inside the geometry was one who was walking (measured, all forty of
 * them: `tools/probe-routine-stuck.mjs`). That is not a different bug, it is the
 * same one moving: a still photograph cannot tell a monster wading through the
 * fountain from one standing in it, and a photograph is what this game is judged
 * on.
 *
 * The cause is one level down and belongs to the world crew: `lineIsClear` and
 * `findWalkPath` route over a mask built with `CLEARANCE = 0.45` — half a
 * monster, chosen so a PATH does not clip a corner, which is a different and
 * smaller promise than "no part of the art overlaps". Widening that constant
 * would move the kid's own monster too and is not this crew's call.
 *
 * So the two functions below are how THIS crew asks for the same walk with room
 * to spare, using nothing but the world's own published query:
 *
 *   `roomyLine`  — the straight-line test, run down three rails a body's width
 *                  apart instead of one. A walk that would brush the fountain is
 *                  no longer "clear", so it gets routed instead.
 *   `roomyNear`  — push one waypoint out until a whole monster fits, searched
 *                  finely (25 cm) so the shape of the route survives. The 2 m
 *                  tile lattice `walkableNear` uses is right for a destination
 *                  and much too coarse for a corner.
 */
export function roomyLine(a: Vec3, b: Vec3): boolean {
  const dx = b.x - a.x
  const dz = b.z - a.z
  const dist = Math.hypot(dx, dz)
  if (dist < 0.001) return isTownWalkable(a.x, a.z)
  // The two rails sit either side of the walk, a body's width out.
  const px = (-dz / dist) * BODY
  const pz = (dx / dist) * BODY
  const steps = Math.max(1, Math.ceil(dist / 0.4))
  for (let i = 0; i <= steps; i++) {
    const t = i / steps
    const x = a.x + dx * t
    const z = a.z + dz * t
    if (!isTownWalkable(x, z)) return false
    if (!isTownWalkable(x + px, z + pz)) return false
    if (!isTownWalkable(x - px, z - pz)) return false
  }
  return true
}

/** A point near `p` with a whole body of room. `p` itself if there is none. */
export function roomyNear(p: Vec3, radius = 1.75): Vec3 {
  if (bodyFits(p.x, p.z)) return { x: p.x, y: 0, z: p.z }
  for (let r = 0.25; r <= radius; r += 0.25) {
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2
      const x = p.x + Math.cos(a) * r
      const z = p.z + Math.sin(a) * r
      if (bodyFits(x, z)) return { x, y: 0, z }
    }
  }
  return { x: p.x, y: 0, z: p.z }
}

/** The body ring alone, without the placement-grid half. Used while routing. */
function bodyFits(x: number, z: number): boolean {
  if (!isTownWalkable(x, z)) return false
  for (const [dx, dz] of BODY_RING) {
    if (!isTownWalkable(x + dx, z + dz)) return false
  }
  return true
}

/**
 * DIAGNOSTIC ONLY — how far is the nearest thing a monster cannot walk on?
 *
 * The number the gate needs to see a monster in the fountain: "unwalkable" is a
 * yes/no that a 1 m mask rounds in the game's favour, and a distance is not.
 * Sampled, not solved, so it is honest to the resolution it prints (5 cm) and
 * nothing more. Never called from the game — only from `window.__mtDay`.
 */
export function clearanceAt(grid: Grid, x: number, z: number, max = 2.5): number {
  if (!pointStandable(grid, x, z)) return 0
  const step = 0.05
  for (let r = step; r <= max; r += step) {
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2
      if (!isTownWalkable(x + Math.cos(a) * r, z + Math.sin(a) * r)) return Number(r.toFixed(2))
    }
  }
  return max
}

/**
 * The nearest cell to `p` a monster can actually stand on, searched outwards.
 * This is what makes a player-built layout safe: build a wall across the
 * pavement and the neighbour stands beside it instead of inside it — and, since
 * the standability test grew its second half, what keeps a neighbour out of the
 * water when the DATA points at the water.
 *
 * NEAREST IN THE RING, NOT FIRST SCANNED. The loop walks dz from -r and dx from
 * -r, so taking the first hit always preferred the north-west cell of the ring;
 * the same trap `src/monster/walker.ts` documents at `nearestWalkable`. Scoring
 * the ring costs at most a few dozen distance tests on a spot that is only
 * resolved when a neighbour changes activity.
 */
function ringSearch(
  grid: Grid,
  p: Vec3,
  radius: number,
  fits: (x: number, z: number) => boolean,
): Vec3 | null {
  const c = grid.worldToCell(p.x, p.z, 0)
  const inBounds = (gx: number, gz: number): boolean =>
    gx >= 0 && gz >= 0 && gx < grid.spec.width && gz < grid.spec.depth
  for (let r = 1; r <= radius; r++) {
    let best: Vec3 | null = null
    let bestD = Infinity
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue
        const gx = c.gx + dx
        const gz = c.gz + dz
        if (!inBounds(gx, gz)) continue
        const w = grid.cellToWorld({ gx, gy: 0, gz })
        if (!fits(w.x, w.z)) continue
        const d = (w.x - p.x) * (w.x - p.x) + (w.z - p.z) * (w.z - p.z)
        if (d < bestD) {
          bestD = d
          best = w
        }
      }
    }
    if (best !== null) return best
  }
  return null
}

export function walkableNear(grid: Grid, p: Vec3, radius = 8): Vec3 {
  if (standable(grid, p.x, p.z)) return { x: p.x, y: 0, z: p.z }
  const room = ringSearch(grid, p, radius, (x, z) => standable(grid, x, z))
  if (room !== null) return room
  /*
   * NOTHING WITHIN EIGHT TILES HAS ROOM FOR A WHOLE MONSTER — a very built-up
   * town, or a doorway a kid has bricked most of the way up. Somewhere they
   * merely FIT still beats nowhere: LAW #2 says a neighbour is never refused,
   * and the tidy pass will keep looking on the next sweep as the town changes.
   */
  const tight = ringSearch(grid, p, radius, (x, z) => pointStandable(grid, x, z))
  if (tight !== null) return tight
  // Standing where they are is always allowed — never stuck, never removed.
  return { x: p.x, y: 0, z: p.z }
}

/** Half the walkable world, in world units. */
function halfSpan(grid: Grid): number {
  return (Math.min(grid.spec.width, grid.spec.depth) * grid.tileSize) / 2
}

function onRing(node: SceneNode, bearing: number, reach: number): Vec3 {
  const c = townCentre(node)
  const r = halfSpan(node.grid) * Math.max(0, Math.min(1, reach))
  return { x: c.x + Math.cos(bearing) * r, y: 0, z: c.z + Math.sin(bearing) * r }
}

/**
 * Where should a monster stand for this place tag? Null means "nowhere in
 * particular" — see the header.
 */
export function placeSpot(node: SceneNode, where: string): Vec3 | null {
  const row = ROUTINE_PLACES.find((p) => p.id === where)
  if (row === undefined) return null

  if (row.landmark !== undefined) {
    const l = landmarks().find((x) => x.id === row.landmark)
    if (l !== undefined) {
      // Stand a couple of steps back from the door, toward the middle of town,
      // so a neighbour is on the pavement rather than in the doorway a kid is
      // about to tap.
      const c = townCentre(node)
      const dx = c.x - l.at.x
      const dz = c.z - l.at.z
      const d = Math.hypot(dx, dz) || 1
      return walkableNear(node.grid, {
        x: l.at.x + (dx / d) * 2.4,
        y: 0,
        z: l.at.z + (dz / d) * 2.4,
      })
    }
  }

  if (row.reach === undefined) return null
  return walkableNear(node.grid, onRing(node, row.bearing ?? 0, row.reach))
}

/**
 * A NEIGHBOUR'S OWN FRONT STEP. Deterministic from their id, so the same
 * monster goes home to the same doorway every night for ever — including after
 * a reload, which is what makes the town feel like it has addresses.
 *
 * Spread by the golden angle rather than evenly, so seven neighbours do not all
 * turn in at the same corner and the street empties from several directions at
 * once.
 */
export function homeSpot(node: SceneNode, npcId: string): Vec3 {
  let h = 0
  for (let i = 0; i < npcId.length; i++) h = (h * 31 + npcId.charCodeAt(i)) >>> 0
  const bearing = (h % 997) * 0.618034 * Math.PI * 2
  return walkableNear(node.grid, onRing(node, bearing, HOME_REACH))
}

/** Somewhere pleasant to be on the ring road, spread the same way. */
export function streetSpot(node: SceneNode, npcId: string, hour: number): Vec3 {
  let h = 0
  for (let i = 0; i < npcId.length; i++) h = (h * 31 + npcId.charCodeAt(i)) >>> 0
  const bearing = ((h % 997) * 0.618034 + hour * 0.13) * Math.PI * 2
  return walkableNear(node.grid, onRing(node, bearing, 0.62))
}
